import { currentLocale } from "./i18n.ts";

const UNITS = ["byte", "kilobyte", "megabyte", "gigabyte"] as const;

/**
 * Sizes in binary steps, as the upload limit counts them (1 MB = 1 MiB), with the locale's unit
 * names and separators: "1.5 MB", "1,5 Mo".
 */
export function formatBytes(bytes: number): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  if (unit === 0) {
    // CLDR's short byte unit reads "byte" in English; the symbol is what people expect.
    return `${new Intl.NumberFormat(currentLocale()).format(value)} ${currentLocale() === "fr" ? "o" : "B"}`;
  }
  return new Intl.NumberFormat(currentLocale(), {
    style: "unit",
    unit: UNITS[unit],
    unitDisplay: "short",
    maximumFractionDigits: value >= 10 ? 0 : 1,
  }).format(value);
}

/** A count with the locale's grouping: "8,420", "8 420". */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat(currentLocale()).format(value);
}

/** A share from 0 to 1 as a whole percentage: "75%", "75 %". */
export function formatPercent(share: number): string {
  return new Intl.NumberFormat(currentLocale(), {
    style: "percent",
    maximumFractionDigits: 0,
  }).format(share);
}

/** A day: "3 Mar 2026", "3 mars 2026". */
export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(currentLocale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));
}

/** A day in a dense column: "3 Mar", "3 mars" this year; "3 Mar 2025" for any other. */
export function formatShortDate(iso: string, now = new Date()): string {
  const date = new Date(iso);
  return new Intl.DateTimeFormat(currentLocale(), {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  }).format(date);
}

/** A moment: "3 Mar 2026, 14:05", "3 mars 2026, 14:05". */
export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat(currentLocale(), {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

/** "Anne, Bertrand and Chloé" / "Anne, Bertrand et Chloé". */
export function formatList(items: readonly string[]): string {
  return new Intl.ListFormat(currentLocale(), { type: "conjunction" }).format(items);
}

/** Alternatives in the reader's language: "A or B", "A ou B". */
export function formatDisjunction(items: readonly string[]): string {
  return new Intl.ListFormat(currentLocale(), { type: "disjunction" }).format(items);
}

/** A country from its ISO 3166-1 code, in the reader's language: "France", "Allemagne". */
export function formatCountry(code: string): string {
  try {
    return new Intl.DisplayNames(currentLocale(), { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * How long ago, for a feed: "40 min ago", "3 hr ago", "yesterday" within a week, then the
 * short date ("3 Mar"). A moment in the future reads as now.
 */
export function formatAgo(iso: string, now = new Date()): string {
  const seconds = Math.max(0, (now.getTime() - new Date(iso).getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(currentLocale(), { numeric: "auto", style: "short" });
  if (seconds < 60) {
    return rtf.format(0, "second");
  }
  if (seconds < 3600) {
    return rtf.format(-Math.floor(seconds / 60), "minute");
  }
  if (seconds < 86_400) {
    return rtf.format(-Math.floor(seconds / 3600), "hour");
  }
  if (seconds < 7 * 86_400) {
    return rtf.format(-Math.floor(seconds / 86_400), "day");
  }
  return formatShortDate(iso, now);
}
